import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { colors, spacing, typography, borderRadius } from '../../tokens';
import type { IconProps } from '../../icons/types';

export interface PillProps {
  /**
   * The text label displayed in the pill
   */
  label: string;

  /**
   * Optional icon component to display before the label
   */
  icon?: React.ComponentType<IconProps>;

  /**
   * Whether the pill is in a selected state
   */
  selected?: boolean;

  /**
   * Whether to show only the label when selected (hides icon)
   */
  selectedLabelOnly?: boolean;

  /**
   * Callback when the pill is pressed
   */
  onPress?: () => void;

  /**
   * Whether the pill is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const Pill: React.FC<PillProps> = ({
  label,
  icon: Icon,
  selected = false,
  selectedLabelOnly = false,
  onPress,
  disabled = false,
  testID,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  // Determine if we should show the icon
  const showIcon = Icon && !(selected && selectedLabelOnly);

  // Determine background color based on state
  const getBackgroundColor = () => {
    if (selected) {
      return colors.neutral[900]; // #020202
    }
    if (isHovered && !disabled) {
      return colors.neutral[200]; // #ECEEf1
    }
    return colors.neutral[100]; // #F3F3F3
  };

  // Determine text color based on state
  const getTextColor = () => {
    if (selected) {
      return colors.neutral[0]; // white
    }
    return colors.neutral[900]; // #020202
  };

  // Determine icon color based on state
  const getIconColor = () => {
    if (selected) {
      return colors.neutral[0]; // white
    }
    return colors.neutral[900]; // #020202
  };

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.container,
        { backgroundColor: getBackgroundColor() },
        disabled && styles.disabled,
      ]}
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
    >
      {showIcon && (
        <View style={styles.iconContainer}>
          <Icon size={16} color={getIconColor()} />
        </View>
      )}
      <Text style={[styles.label, { color: getTextColor() }]}>
        {label}
      </Text>
    </Pressable>
  );
};

Pill.displayName = 'Pill';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md, // 16px
    paddingVertical: spacing.xs, // 8px
    borderRadius: borderRadius.full, // 20px
    gap: spacing.xxs, // 4px
    alignSelf: 'flex-start',
  },
  iconContainer: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    ...typography.body.md,
    fontWeight: '400',
  },
  disabled: {
    opacity: 0.5,
  },
});
